//#region Util Imports

import * as _ from 'underscore'
import * as $ from 'jquery'
import * as moment from 'moment'

//#endregion

//#region DevExtreme Imports

import DevExpress from 'devextreme'

//#endregion

import { CommonHelper } from '../../../../../helpers/common-helper'
import { ConvertHelper } from '../../../../../helpers/convert-helper'
import { Dictionary } from "../../../../../helpers/dictionary"
import { TypeHelper } from '../../../../../helpers/type-helper'

import { Enums } from "../../../../../models/common/enums"
import { Measure } from "../../../../../models/custom/measure"
import { MeasureCollection } from "../../../../../models/custom/measure-collection"
import { OpDataFormatGroupDetails } from '../../../../../models/core/op-data-format-group-details'
import { OpDataTypeInGroup } from "../../../../../models/core/op-data-type-in-group"

import { IIMRChartExtensionController } from "../../chart.controller"
import { SeriesInfo, MeasureCollectionChartExtension } from './measure-collection.chart.extension'


// Private Types

type ChartDataPoint = {
    time: Date
} & { [index: string]: any }

type DataTypeUnitGrouping = {
    separate: OpDataTypeInGroup[],
    unitGrouped: Dictionary<OpDataTypeInGroup[]>
}

type MinMaxDataTypeValues = {
    min: number,
    max: number
}

type SeriesTag = {
    dataTypeInGroup: OpDataTypeInGroup
}

// Private Types End


export class MeasureCollectionChartExtensionChart {

    // Members

    private chart: DevExpress.viz.dxChart
    private groupedDataTypes: DataTypeUnitGrouping
    private separateYAxisDataTypeInGroupList: OpDataTypeInGroup[]
    private hiddenYAxisDataTypeInGroupList: OpDataTypeInGroup[]
    private chartController: IIMRChartExtensionController
    private seriesInfos: SeriesInfo[]

    // Members End


    // Ctor

    constructor(private baseExtension: MeasureCollectionChartExtension) { }

    // Ctor End


    public getOptions(): DevExpress.viz.charts.dxChartOptions {
        return {
            autoHidePointMarkers: false,
            palette: "Office",
            scrollingMode: 'all',
            synchronizeMultiAxes: false,
            useAggregation: false,
            zoomingMode: 'all',

            adaptiveLayout: {
                width: 0,
                height: 0
            },

            animation: {
                enabled: false
            },

            argumentAxis: {
                argumentType: 'datetime',
                minValueMargin: 0.1,
                maxValueMargin: 0.1,
                axisDivisionFactor: 40,

                grid: {
                    visible: true
                },

                tick: {
                    visible: true,
                    length: 10,
                    color: 'blue',
                    width: 2
                }
            },


            commonAxisSettings: {
                tick: {
                    visible: false,
                },

                label: {
                    wordWrap: 'none'
                }
            },

            commonSeriesSettings: {
                tagField: "info",
                argumentField: "time",
                ignoreEmptyPoints: true,
                axis: "number",

                point: {
                    size: 4,
                    hoverStyle: {
                        size: 3
                    }
                }
            },

            dataPrepareSettings: {
                checkTypeForAllData: true
            },

            legend: {
                columnItemSpacing: 2,
                horizontalAlignment: 'center',
                itemTextPosition: 'right',
                orientation: 'horizontal',
                paddingLeftRight: 20,
                rowItemSpacing: 2,

                customizeText: (e: any) => this.customizeLegendText(e)
            },

            tooltip: {
                enabled: true,
                zIndex: 2001, // So tooltips are visible above fullscreen container (which has z-index=2000)

                customizeTooltip: (e: any) => this.customizeTooltip(e)
            },

            valueAxis: {
                minValueMargin: 0.01,
                maxValueMargin: 0.01,
            },

            onInitialized: (e: any) => this.chart = e.component,
            onLegendClick: (e: any) => this.onLegendClick(e)
        }
    }

    public initialize(chartController: IIMRChartExtensionController): JQueryPromise<void> {
        this.chartController = chartController

        return $
            .when(this.getSeriesSeparateYAxisDataTypes(), this.getHiddenYAxisDataTypes())
            .then((separateYAxisDataTypeInGroupList: OpDataTypeInGroup[], hiddenYAxisDataTypeInGroupList: OpDataTypeInGroup[]) => {
                this.separateYAxisDataTypeInGroupList = separateYAxisDataTypeInGroupList
                this.hiddenYAxisDataTypeInGroupList = hiddenYAxisDataTypeInGroupList
            })
    }

    public refresh(measureCollection: MeasureCollection[], seriesInfos: SeriesInfo[]): JQueryPromise<void> {
        this.groupedDataTypes = this.groupDataTypes(seriesInfos, this.separateYAxisDataTypeInGroupList)
        this.seriesInfos = seriesInfos

        return $
            .when<any>(this.createSeries(this.groupedDataTypes), this.createValueAxes(this.groupedDataTypes))
            .then((seriesList: DevExpress.viz.ChartSeries[], axisList: DevExpress.viz.ChartSeries[]) => {
                const chartDataPoints: ChartDataPoint[] = this.createChartDataPoints(measureCollection)

                if (this.chart != null) {
                    this.chart.beginUpdate()
                    this.chart.option('series', seriesList)
                    this.chart.option('valueAxis', axisList)
                    this.chart.option('dataSource', chartDataPoints)

                    this.hideSeries()
                    this.hideHiddenYAxis()

                    _.forEach(seriesList, (series: DevExpress.viz.ChartSeries) => {
                        this.updateValueAxisMinMaxValues(series)
                    })

                    this.chart.endUpdate()
                }
            })
    }

    // Series

    private createSeries(grouping: DataTypeUnitGrouping): JQueryPromise<DevExpress.viz.ChartSeries[]> {
        const seriesList: DevExpress.viz.ChartSeries[] = []

        grouping.separate.forEach((separateDataTypeInGroup: OpDataTypeInGroup) => {
            const series: DevExpress.viz.ChartSeries = this.createBaseSeries(separateDataTypeInGroup)

            series.axis = separateDataTypeInGroup.idDataType.toString()

            seriesList.push(series)
        })

        grouping.unitGrouped.forEach((key: string, value: OpDataTypeInGroup[]) => {
            value.forEach((groupedDataTypeInGroupList: OpDataTypeInGroup) => {
                const series: DevExpress.viz.ChartSeries = this.createBaseSeries(groupedDataTypeInGroupList)

                series.axis = groupedDataTypeInGroupList.dataType.unit.idUnit.toString()

                seriesList.push(series)
            });
        })

        return $.when(...seriesList).then((...result: DevExpress.viz.ChartSeries[]) => result)
    }

    private createBaseSeries(dataTypeInGroup: OpDataTypeInGroup): DevExpress.viz.ChartSeries {
        const seriesInfo: SeriesInfo = _.find(this.seriesInfos, (seriesInfo: SeriesInfo) => seriesInfo.dataTypeInGroup.idDataType === dataTypeInGroup.idDataType)

        if (seriesInfo != null) {
            const seriesTag: SeriesTag = {
                dataTypeInGroup,
            }

            const cssColor: string = seriesInfo.color != null ? ConvertHelper.knownColorEnumToRGBAColor(seriesInfo.color) : null
            const dxChartType: string = this.baseExtension.getDevExpressChartType(seriesInfo.type) || 'line'

            const seriesConfig: DevExpress.viz.ChartSeries = {
                name: dataTypeInGroup.toString(),
                color: cssColor,
                tag: seriesTag,
                type: dxChartType as any,
                valueField: dataTypeInGroup.idDataType.toString(),
                visible: true,
            }

            return seriesConfig
        }
    }


    private getSeriesConfigByIdDataType(idDataType: number): DevExpress.viz.ChartSeries {
        const seriesConfigs: DevExpress.viz.ChartSeries[] = this.chart.option("series")

        return _.find(seriesConfigs, (seriesConfigs: DevExpress.viz.ChartSeries) => {
            return this.getSeriesDataType(seriesConfigs).idDataType === idDataType
        })
    }

    private getSeriesDataType(seriesConfig: DevExpress.viz.ChartSeries): OpDataTypeInGroup {
        const tag: SeriesTag = seriesConfig.tag

        return tag.dataTypeInGroup
    }


    private hideSeries() {
        _.chain(this.seriesInfos)
            .filter((seriesInfo: SeriesInfo) => {
                return !seriesInfo.visible
            })
            .map((seriesInfo: SeriesInfo) => {
                return seriesInfo.dataTypeInGroup.idDataType
            })
            .forEach((idDataType: number) => {
                this.chartController.toggleSeries(this.getSeriesConfigByIdDataType(idDataType))
            })
    }

    // Series End


    // Axes

    private createValueAxes(grouping: DataTypeUnitGrouping): DevExpress.viz.dxChartValueAxis[] {
        const axes: DevExpress.viz.dxChartValueAxis[] = []

        grouping.separate.forEach((dataTypeInGroup: OpDataTypeInGroup) => {
            const axis: DevExpress.viz.dxChartValueAxis = this.createBaseValueAxis(dataTypeInGroup)

            axis.name = dataTypeInGroup.idDataType.toString();

            axes.push(axis)
        })

        grouping.unitGrouped.forEach((key: string, value: OpDataTypeInGroup[]) => {
            const idUnit: number = Number(key)
            const dataTypeInGroup: OpDataTypeInGroup = _.first(grouping.unitGrouped.tryGetValue(idUnit))
            const axis: DevExpress.viz.dxChartValueAxis = this.createBaseValueAxis(dataTypeInGroup)

            axis.name = idUnit.toString();

            axes.push(axis)
        })

        _.forEach(axes, (axis: DevExpress.viz.dxChartValueAxis, index: number) => {
            axis.position = (index % 2) != 0 ? "right" : "left"
        })

        return axes
    }

    private createBaseValueAxis(dataTypeInGroup: OpDataTypeInGroup, isVisible: boolean = true): DevExpress.viz.dxChartValueAxis {
        const axis: DevExpress.viz.dxChartValueAxis = {
            showZero: false,
            title: {
                text: this.baseExtension.getUnitWrappedText(dataTypeInGroup),
            },
            valueType: "numeric",
            visible: true,

            label: {
                visible: true,
            },

            grid: {
                visible: true,
            }
        }

        if (dataTypeInGroup.dataType.idDataTypeClass === Enums.DataTypeClass.Datetime) {
            axis.valueType = 'string'

            _.assign(axis.label, {
                customizeText: (options) => {
                    return moment(options.value).format(window['simax'].getDateFormat('DATE_TIME_FORMAT_MOMENTJS'))
                }
            })
        } else {
            _.assign(axis.label, {
                customizeText: (options) => {
                    if (_.isFinite(options.value)) {
                        return this.baseExtension.formatValue(+options.value, dataTypeInGroup.idDataType, Enums.DataTypeClass.Integer)
                    }

                    return options.value
                }
            })
        }

        return axis
    }

    private hideHiddenYAxis(): void {
        _.forEach(this.hiddenYAxisDataTypeInGroupList, (dataTypeInGroup: OpDataTypeInGroup) => {
            const seriesConfig: DevExpress.viz.ChartSeries = this.getSeriesConfigByIdDataType(dataTypeInGroup.idDataType)

            if (seriesConfig != null) {
                this.chartController.hideSeriesAxisPermanently(seriesConfig)
            }
        })
    }

    private updateValueAxisMinMaxValues(seriesConfig: DevExpress.viz.ChartSeries): void {
        if (this.chart != null && seriesConfig != null) {
            const seriesDataType: OpDataTypeInGroup = this.getSeriesDataType(seriesConfig)
            const axis: DevExpress.viz.dxChartValueAxis = this.getAxisConfigByName(seriesConfig.axis)

            if (axis != null && seriesDataType != null) {
                let minMaxValuesPromise: JQueryPromise<MinMaxDataTypeValues>

                if (this.isDataTypeUnitGrouped(seriesDataType.idDataType)) {
                    minMaxValuesPromise = this.getDataTypeMinMaxValues(this.getVisibleUnitGrouping(seriesDataType.dataType.idUnit))
                }
                else {
                    minMaxValuesPromise = this.getDataTypeMinMaxValues(this.isDataTypeVisible(seriesDataType.idDataType) ? [seriesDataType] : null)
                }

                minMaxValuesPromise.then((minMaxValues: MinMaxDataTypeValues) => {
                    if (minMaxValues != null) {
                        axis.valueMarginsEnabled = false
                        axis.visualRange = {
                            startValue: minMaxValues.min,
                            endValue: minMaxValues.max
                        }
                    }
                    else {
                        axis.valueMarginsEnabled = true
                        axis.visualRange = null
                    }

                    this.chart.option('valueAxis', this.chart.option('valueAxis'))
                })
            }
        }
    }

    private getAxisConfigByName(axisName: string): DevExpress.viz.dxChartValueAxis {
        const axisConfigs: DevExpress.viz.dxChartValueAxis[] = this.chart.option('valueAxis')

        return _.find(axisConfigs, (axisConfig: DevExpress.viz.dxChartValueAxis) => axisConfig.name === axisName)
    }

    // Axes End


    // Chart

    private customizeLegendText(e: any): string {
        if (this.chart != null) {
            const series: DevExpress.viz.baseSeriesObject = this.chart.getSeriesByPos(e.seriesIndex)

            if (series != null) {
                const tag: SeriesTag = series.tag as any as SeriesTag

                if (tag != null && tag.dataTypeInGroup != null && tag.dataTypeInGroup.dataType != null && tag.dataTypeInGroup.dataType.unit != null) {
                    const wrappedUnitText: string = this.baseExtension.getUnitWrappedText(tag.dataTypeInGroup)

                    if (!TypeHelper.isNullOrEmpty(wrappedUnitText)) {
                        return `${e.seriesName} ${wrappedUnitText}`
                    }
                }
            }
        }

        return e.seriesName
    }

    private customizeTooltip(point: any): any {
        if (this.chart.option('tooltip.shared') && _.isArray(point.points)) {
            const pointObjects: any[] = point.points
            const container: JQuery = $('<div/>')

            let argument: string = TypeHelper.emptyString

            pointObjects.forEach((point: any) => {
                const dataType: OpDataTypeInGroup = this.getSeriesDataType(point.point.series)

                if (dataType != null) {
                    const currentArgument = moment(point.argument).format(window['simax'].getDateFormat('DATE_TIME_FORMAT_MOMENTJS'))
                    const color: any = point.point.getColor()
                    const value: any = point.valueText
                    const unitText: string = this.baseExtension.getUnitWrappedText(dataType)

                    if (argument != currentArgument) {
                        argument = currentArgument

                        container.append($('<div/>').text(argument).css('font-weight', 700))
                    }

                    container.append($('<div/>').text(`${dataType.toString()}: ${value} ${unitText}`).css('font-weight', 700).css('color', color))
                }
            })

            return { html: container.html() }
        }

        const pointObject: DevExpress.viz.chartPointObject = point.point
        const dataType: OpDataTypeInGroup = this.getSeriesDataType(pointObject.series)

        if (dataType != null) {
            const argument = moment(point.argument).format(window['simax'].getDateFormat('DATE_TIME_FORMAT_MOMENTJS'))
            const color: any = point.point.getColor()
            const value: any = point.valueText
            const unitText: string = this.baseExtension.getUnitWrappedText(dataType)

            const container: JQuery = $('<div/>')
                .append($('<div/>').text(argument).css('font-weight', 700))
                .append($('<div/>').text(`${dataType.toString()}: ${value} ${unitText}`).css('font-weight', 700).css('color', color))

            return { html: container.html() }
        }
    }

    private isDataTypeVisible(idDataType: number): boolean {
        const seriesConfig = this.getSeriesConfigByIdDataType(idDataType)

        return seriesConfig != null && seriesConfig.visible
    }

    private isDataTypeUnitGrouped(idDataType: number): boolean {
        return !_.contains(this.groupedDataTypes.separate.map((d) => d.idDataType), idDataType)
    }

    private getVisibleUnitGrouping(idUnit: number): OpDataTypeInGroup[] {
        const dataTypeInGroupList: OpDataTypeInGroup[] = this.groupedDataTypes.unitGrouped.tryGetValue(idUnit)

        return _.filter(dataTypeInGroupList, (dataTypeInGroup: OpDataTypeInGroup) => {
            const seriesConfig = this.getSeriesConfigByIdDataType(dataTypeInGroup.idDataType)

            if (seriesConfig != null && seriesConfig.visible) {
                return !_.contains(this.hiddenYAxisDataTypeInGroupList.map(a => a.idDataType), dataTypeInGroup.idDataType)
            }

            return false
        })
    }

    private onLegendClick(e: any): void {
        const currentSeries: DevExpress.viz.ChartSeries = e != null ? e.target : null

        this.updateValueAxisMinMaxValues(currentSeries)

        const state: any = this.chartController.getExtensionState() || {}
        const allSeriesConfigs: DevExpress.viz.ChartSeries[] = this.chart.option('series')

        if (_.isArray(allSeriesConfigs)) {
            state.visibleIdDataTypes = _
                .chain(allSeriesConfigs)
                .filter((seriesConfig: DevExpress.viz.ChartSeries) => {
                    return seriesConfig.visible
                })
                .map((seriesConfig: DevExpress.viz.ChartSeries) => {
                    return this.getSeriesDataType(seriesConfig).idDataType
                })
                .value()

            this.chartController.storeExtensionState(state)
        }
    }

    // Chart End


    // DataTypes 

    private createChartDataPoints(measureCollection: MeasureCollection[]): ChartDataPoint[] {
        const dataPoints: ChartDataPoint[] = []

        measureCollection.forEach((measureCollection: MeasureCollection) => {
            const points: ChartDataPoint[] = measureCollection.measures.map((measure: Measure) => {
                return {
                    time: measure.time,
                    [measureCollection.idDataType]: measure.value
                }
            })

            dataPoints.push(...points)
        })

        return dataPoints
    }

    private getDataTypeMinMaxValues(dataTypeInGroupList: OpDataTypeInGroup[]): JQueryPromise<MinMaxDataTypeValues> {
        return this.chartController
            .getUserSession()
            .getDataProvider().Operator
            .getOperatorSettings(this.chartController.getUserSession().getIdLoggedOperator(), [Enums.SimaxOperatorSetting.DataTypeFormatGroup])
            .then((dataFormatGroupDetailsList: OpDataFormatGroupDetails[]) => {
                let minValues: number[] = []
                let maxValues: number[] = []

                if (_.isArray(dataTypeInGroupList)) {
                    dataTypeInGroupList.forEach((dataTypeInGroup: OpDataTypeInGroup) => {
                        const existingDataTypeFormatGroup: OpDataFormatGroupDetails = _.find(dataFormatGroupDetailsList, (dataFormatGroupDetails: OpDataFormatGroupDetails) => {
                            return dataFormatGroupDetails.idDataType === dataTypeInGroup.idDataType
                        })

                        if (existingDataTypeFormatGroup != null && existingDataTypeFormatGroup.dataTypeFormatOut != null) {
                            const currentMin = existingDataTypeFormatGroup.dataTypeFormatOut.numberMinValue
                            const currentMax = existingDataTypeFormatGroup.dataTypeFormatOut.numberMaxValue

                            if (currentMax != null && currentMin != null) {
                                minValues.push(currentMin)
                                maxValues.push(currentMax)
                            }
                        }
                        else if (dataTypeInGroup.dataType.dataTypeFormat != null) {
                            const currentMin = dataTypeInGroup.dataType.dataTypeFormat.numberMinValue
                            const currentMax = dataTypeInGroup.dataType.dataTypeFormat.numberMaxValue

                            if (currentMax != null && currentMin != null) {
                                minValues.push(currentMin)
                                maxValues.push(currentMax)
                            }
                        }
                    })
                }

                if (_.size(minValues) > 0 && _.size(maxValues)) {
                    return {
                        max: Math.max(...maxValues),
                        min: Math.min(...minValues)
                    }
                }

                return null
            })
    }

    private getHiddenYAxisDataTypes(): JQueryPromise<OpDataTypeInGroup[]> {
        return this.chartController
            .getUserSession()
            .getDataProvider().DataTypeGroup
            .getDataTypeInGroup(Enums.DataTypeGroup.WEBSIMAX_DATA_TYPE_HIDDEN_Y_AXIS)
    }

    private getSeriesSeparateYAxisDataTypes(): JQueryPromise<OpDataTypeInGroup[]> {
        return this.chartController
            .getUserSession()
            .getDataProvider().DataTypeGroup
            .getDataTypeInGroup(Enums.DataTypeGroup.WEBSIMAX_DATA_TYPE_SERIES_SEPARATE_Y_AXIS)
    }

    private groupDataTypes(seriesInfo: SeriesInfo[], separateDataTypeInGroupList: OpDataTypeInGroup[]): DataTypeUnitGrouping {
        const dataTypeUnitDict: Dictionary<OpDataTypeInGroup[]> = new Dictionary<OpDataTypeInGroup[]>(null)
        const separateDataTypes: OpDataTypeInGroup[] = []

        seriesInfo
            .map((seriesInfo: SeriesInfo) => seriesInfo.dataTypeInGroup)
            .forEach((dataTypeInGroup: OpDataTypeInGroup) => {
                if (dataTypeInGroup.dataType != null) {
                    const isSeparateDataType: boolean = _.some(separateDataTypeInGroupList, (separateDataTypeInGroup: OpDataTypeInGroup) =>
                        separateDataTypeInGroup.idDataType === dataTypeInGroup.idDataType)

                    if (isSeparateDataType) {
                        separateDataTypes.push(dataTypeInGroup)
                    } else {

                        if (dataTypeInGroup.dataType.unit != null) {
                            const idUnit: number = dataTypeInGroup.dataType.unit.idUnit

                            if (!dataTypeUnitDict.containsKey(idUnit)) {
                                dataTypeUnitDict.add(idUnit, [])
                            }

                            dataTypeUnitDict
                                .tryGetValue(idUnit)
                                .push(dataTypeInGroup)
                        }
                    }
                }
            })

        const result: DataTypeUnitGrouping = {
            separate: [],
            unitGrouped: new Dictionary<OpDataTypeInGroup[]>(null)
        }

        result.separate.push(...separateDataTypes)

        dataTypeUnitDict.forEach((key: string, value: OpDataTypeInGroup[]) => {
            if (_.size(value) === 1) {
                result.separate.push(...value)
            } else {
                result.unitGrouped.add(key, value)
            }
        })

        return result
    }

    // DataTypes End
}